import { HTTPError } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';

export const WEB_TO_CASE_ENDPOINT = 'https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8';
export const WEB_TO_CASE_ORG_ID = '00D7F000002CrgE';
export const WEB_TO_CASE_HOST = 'aitravel.jp';
export const DEFAULT_INQUIRY_ERROR = '通信エラーが発生しました。しばらく時間をおいて再度お試しください。';

export type InquiryParams = {
  company: string;
  name: string;
  email: string;
  position: string;
  subject: string;
  description: string;
  tel: string;
};

export type InquirySubmitResult = {
  finish: boolean;
  errors: string[];
};

export const shouldUseWebToCase = (hostname: string): boolean => hostname === WEB_TO_CASE_HOST;

export const buildInquiryUrlBase = (loc: Pick<Location, 'protocol' | 'hostname' | 'port'>): string => {
  const port = loc.port ? `:${loc.port}` : '';
  return `${loc.protocol}//${loc.hostname}${port}`;
};

export const inquiryErrorMessages = (error: unknown): string[] => {
  if (error instanceof HTTPError && error.response?.data.errors) {
    return Array.isArray(error.response.data.errors) ? error.response.data.errors : [error.response.data.errors];
  }
  return [DEFAULT_INQUIRY_ERROR];
};

export const shouldSendWebToCaseAfterInquiryError = (error: unknown): boolean => {
  if (!(error instanceof HTTPError)) {
    return true;
  }
  const status = error.response?.status;
  return status === undefined || status >= 500;
};

export const buildWebToCaseBody = (params: InquiryParams, urlBase: string): URLSearchParams => {
  const body = new URLSearchParams();
  body.set('orgid', WEB_TO_CASE_ORG_ID);
  body.set('retURL', `${urlBase}/inquiries/thanks`);
  body.set('company', params.company);
  body.set('00N7F00000SAUMi', params.position);
  body.set('name', params.name);
  body.set('email', params.email);
  body.set('phone', params.tel);
  body.set('subject', params.subject);
  body.set('description', params.description);
  return body;
};

export const submitWebToCase = async (
  params: InquiryParams,
  urlBase: string,
  fetchImpl: typeof fetch = fetch
): Promise<void> => {
  await fetchImpl(WEB_TO_CASE_ENDPOINT, {
    method: 'POST',
    mode: 'no-cors',
    credentials: 'omit',
    body: buildWebToCaseBody(params, urlBase)
  });
};

export const createExclusiveRunner = () => {
  let running = false;

  return async <T>(task: () => Promise<T>): Promise<T | undefined> => {
    if (running) {
      return undefined;
    }
    running = true;
    try {
      return await task();
    } finally {
      running = false;
    }
  };
};

export const submitInquiryAndMaybeWebToCase = async ({
  params,
  useWebToCase,
  urlBase,
  postInquiry,
  sendWebToCase = submitWebToCase
}: {
  params: InquiryParams;
  useWebToCase: boolean;
  urlBase: string;
  postInquiry: (params: InquiryParams) => Promise<unknown>;
  sendWebToCase?: (params: InquiryParams, urlBase: string) => Promise<void>;
}): Promise<InquirySubmitResult> => {
  let inquiryError: unknown;

  try {
    await postInquiry(params);
  } catch (error) {
    inquiryError = error;
  }

  const shouldSendWebToCase =
    useWebToCase && (inquiryError === undefined || shouldSendWebToCaseAfterInquiryError(inquiryError));

  if (shouldSendWebToCase) {
    try {
      await sendWebToCase(params, urlBase);
    } catch (error) {
      reportError(error);
      if (inquiryError === undefined) {
        return { finish: false, errors: [DEFAULT_INQUIRY_ERROR] };
      }
    }
  }

  if (inquiryError !== undefined) {
    return { finish: false, errors: inquiryErrorMessages(inquiryError) };
  }

  return { finish: true, errors: [] };
};
